import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Users,
  ArrowLeft,
  Banknote,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Phone,
  Mail,
  MapPin,
  FileText,
  Calendar,
  RotateCcw,
  CreditCard,
  Receipt,
  Search,
  CheckCircle2,
  Clock,
  Printer,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  FileSpreadsheet,
  AlertCircle,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import SearchableSelect from '../components/SearchableSelect';
import ThermalReceipt from '../components/ThermalReceipt';
import SaleInvoice from '../components/SaleInvoice';
import { useSettings } from '../context/SettingsContext';
import PharmacyLogo from '../components/PharmacyLogo';
import { formatPakistanDate, formatPakistanDateTime } from '../utils/pakistanTime';

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

function fmtDate(d) {
  if (!d) return '-';
  return formatPakistanDate(d, { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '-';
  return formatPakistanDateTime(d, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtMoney(amount) {
  const n = parseFloat(amount) || 0;
  return `Rs. ${n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [activeTab, setActiveTab] = useState('invoices'); // 'invoices' | 'payments' | 'ledger'

  // Modals
  const [showReceivePayment, setShowReceivePayment] = useState(false);
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [showClearBalance, setShowClearBalance] = useState(false);
  const [showDeleteCustomer, setShowDeleteCustomer] = useState(false);
  const [deletePaymentItem, setDeletePaymentItem] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [printSale, setPrintSale] = useState(null);

  // Forms
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'cash',
    payment_type: 'received',
    sale_id: '',
    notes: '',
  });

  const [editForm, setEditForm] = useState({});

  // Filters for sales
  const [salesSearch, setSalesSearch] = useState('');
  const [salesStatusFilter, setSalesStatusFilter] = useState('all');

  useEffect(() => {
    if (id) {
      loadCustomerData();
    }
  }, [id]);

  const loadCustomerData = async () => {
    setLoading(true);
    try {
      const custId = parseInt(id);
      const custRes = await api.get(`/customers/${custId}`);
      setCustomer(custRes);

      // Load sales for this customer
      const salesRes = await api.get(`/sales?customer_id=${custId}&limit=500`);
      const salesList = Array.isArray(salesRes) ? salesRes : (salesRes?.sales || []);
      setSales(salesList);

      // Load payments for this customer
      const paymentsRes = await api.get(`/customer-payments?customer_id=${custId}&limit=500`);
      const paymentsList = Array.isArray(paymentsRes) ? paymentsRes : (paymentsRes?.payments || []);
      setPayments(paymentsList);

      const refundsRes = await api.get(`/customer-refunds?customer_id=${custId}&limit=500`);
      setRefunds(Array.isArray(refundsRes) ? refundsRes : (refundsRes?.refunds || []));

      if (salesList.length === 0 && paymentsList.length > 0) {
        setActiveTab((prev) => (prev === 'invoices' ? 'payments' : prev));
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const openReceivePaymentModal = (prefillAmount = null) => {
    const currentBal = Number(customer?.balance || 0);
    const amount = prefillAmount !== null
      ? String(prefillAmount)
      : currentBal > 0
        ? String(currentBal)
        : '';
    setPaymentForm({
      amount,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'cash',
      payment_type: 'received',
      sale_id: '',
      notes: '',
    });
    setShowReceivePayment(true);
  };

  const openRefundPaymentModal = (prefillAmount = null) => {
    const currentBal = Number(customer?.balance || 0);
    const weOweBal = currentBal < 0 ? Math.abs(currentBal) : 0;
    const amount = prefillAmount !== null
      ? String(prefillAmount)
      : weOweBal > 0
        ? String(weOweBal)
        : '';
    setPaymentForm({
      amount,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'cash',
      payment_type: 'refund',
      sale_id: '',
      notes: 'Advance refund paid to customer',
    });
    setShowReceivePayment(true);
  };

  const handleReceivePayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      const isRefund = paymentForm.payment_type === 'refund';
      const res = await api.post('/customer-payments', {
        customer_id: parseInt(id),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        payment_type: paymentForm.payment_type || 'received',
        sale_id: paymentForm.payment_type === 'received' && paymentForm.sale_id ? Number(paymentForm.sale_id) : undefined,
        client_request_id: globalThis.crypto?.randomUUID?.() || `cp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        amount: amount,
        notes: paymentForm.notes,
      });

      toast.success(res?.message || (isRefund ? `Advance refund of ${fmtMoney(amount)} paid successfully!` : `Payment of ${fmtMoney(amount)} recorded successfully!`));
      setShowReceivePayment(false);
      await loadCustomerData();
    } catch (err) {
      toast.error(err.message || 'Failed to record payment');
    }
  };

  const openEditModal = () => {
    const bal = Number(customer.balance || 0);
    setEditForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      credit_limit: customer.credit_limit || '0',
      notes: customer.notes || '',
      we_owe: bal < 0 ? String(Math.abs(bal)) : '0',
      they_owe: bal > 0 ? String(bal) : '0',
    });
    setShowEditCustomer(true);
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    try {
      const theyOwe = parseFloat(editForm.they_owe) || 0;
      const weOwe = parseFloat(editForm.we_owe) || 0;
      const updated = await api.put(`/customers/${customer.id}`, {
        name: editForm.name,
        phone: editForm.phone,
        email: editForm.email,
        address: editForm.address,
        credit_limit: parseFloat(editForm.credit_limit) || 0,
        balance: theyOwe - weOwe,
        notes: editForm.notes,
      });

      toast.success('Customer updated successfully');
      setCustomer(updated);
      setShowEditCustomer(false);
      await loadCustomerData();
    } catch (err) {
      toast.error(err.message || 'Failed to update customer');
    }
  };

  const handleClearBalance = async () => {
    try {
      await api.put(`/customers/${customer.id}/clear-balance`);
      toast.success('Balance cleared');
      setShowClearBalance(false);
      await loadCustomerData();
    } catch (err) {
      toast.error(err.message || 'Failed to clear balance');
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await api.delete(`/customers/${customer.id}`);
      toast.success('Customer deleted successfully');
      navigate('/customers');
    } catch (err) {
      toast.error(err.message || 'Failed to delete customer');
    }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentItem) return;
    try {
      await api.delete(`/customer-payments/${deletePaymentItem.id}`);
      toast.success('Payment deleted');
      setDeletePaymentItem(null);
      await loadCustomerData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete payment');
    }
  };

  const openInvoiceDetails = async (sale) => {
    setViewInvoice(sale);
    try {
      const detailed = await api.get(`/sales/${sale.id}`);
      if (detailed) {
        setViewInvoice(detailed);
      }
    } catch {
      // Keep basic sale info
    }
  };

  const handlePrintInvoice = async (sale) => {
    try {
      const detailed = await api.get(`/sales/${sale.id}`);
      const data = detailed || sale;
      setPrintSale({
        ...data,
        customer_name: customer?.name || data.customer_name,
        customer_phone: customer?.phone || data.customer_phone,
        customer_address: customer?.address || data.customer_address,
        customer_balance: customer?.balance,
      });
    } catch {
      setPrintSale({
        ...sale,
        customer_name: customer?.name || sale.customer_name,
        customer_phone: customer?.phone || sale.customer_phone,
        customer_address: customer?.address || sale.customer_address,
        customer_balance: customer?.balance,
      });
    }
  };

  const buildReceiptData = (inv) => {
    if (!inv) return null;
    return {
      invoice: inv.invoice_number || `INV-${inv.id}`,
      date: inv.created_at || new Date(),
      items: (inv.items || []).map((it) => ({
        medicine_name: it.medicine_name || `Medicine #${it.medicine_id}`,
        quantity: it.quantity,
        unit_price: it.unit_price,
        total: it.total,
      })),
      subtotal: parseFloat(inv.subtotal) || 0,
      discount: parseFloat(inv.discount) || 0,
      taxAmount: parseFloat(inv.tax) || 0,
      grandTotal: parseFloat(inv.total) || 0,
      paidAmount: parseFloat(inv.paid_amount) || 0,
      paymentMethod: inv.payment_method || 'cash',
      customer: {
        name: customer?.name || 'Walk-in Customer',
        phone: customer?.phone || '',
      },
      customerBalance: parseFloat(customer?.balance) || 0,
      projectedBalance: parseFloat(customer?.balance) || 0,
      cashier: inv.user_name || 'Admin',
      settings,
    };
  };

  if (loading && !customer) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500 font-medium">Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-emerald-600">
            <ArrowLeft className="h-4 w-4" /> Back to Customers
          </Link>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
          <h2 className="mt-3 text-lg font-bold text-gray-900">Customer Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">The requested customer could not be found or may have been deleted.</p>
          <Link to="/customers" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600">
            View All Customers
          </Link>
        </div>
      </div>
    );
  }

  // Calculations
  const balance = Number(customer.balance || 0);
  const owesUs = balance > 0 ? balance : 0;
  const weOwe = balance < 0 ? Math.abs(balance) : 0;

  const totalInvoiced = sales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
  const totalDirectPayments = payments
    .filter((p) => p.payment_type !== 'refund')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalRefunds = payments
    .filter((p) => p.payment_type === 'refund')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalSalesPaid = sales.reduce((sum, s) => sum + (parseFloat(s.paid_amount) || 0), 0);
  const unpaidSalesCount = sales.filter((s) => s.payment_status === 'unpaid' || s.payment_status === 'partial').length;
  const unpaidSalesTotal = sales
    .filter((s) => s.payment_status === 'unpaid' || s.payment_status === 'partial')
    .reduce((sum, s) => sum + Math.max(0, (parseFloat(s.total) || 0) - (parseFloat(s.paid_amount) || 0)), 0);
  const invoiceRefunds = refunds.reduce((sum, r) => sum + (parseFloat(r.refund_amount) || 0), 0);

  // Filtered sales
  const filteredSales = sales.filter((s) => {
    const matchSearch = (s.invoice_number || '').toLowerCase().includes(salesSearch.toLowerCase());
    const matchStatus = salesStatusFilter === 'all' || s.payment_status === salesStatusFilter;
    return matchSearch && matchStatus;
  });

  // Invoice paid_amount is the canonical settlement amount. A customer receipt
  // may have updated it, so it must never be credited a second time in the
  // statement. Receipt rows below remain audit-only (zero balance impact).
  const ledgerEntries = [
    ...sales.map((s) => ({
      id: `sale-${s.id}`,
      date: s.created_at || s.sale_date,
      type: 'invoice',
      title: `Invoice #${s.invoice_number}`,
      details: `${s.payment_method} sale`,
      debit: parseFloat(s.total) || 0, // customer owes more
      credit: parseFloat(s.paid_amount) || 0, // paid at checkout
      reference: s.invoice_number,
      rawItem: s,
    })),
    ...payments.map((p) => {
      const isRefund = p.payment_type === 'refund';
      return {
        id: `payment-${p.id}`,
        date: p.payment_date || p.created_at,
        type: isRefund ? 'refund' : 'payment',
        title: isRefund ? `Advance Refund / Payout (${p.payment_method})` : `Payment Received (${p.payment_method})`,
        details: `${p.notes || (isRefund ? 'Cash refund recorded' : 'Receipt recorded')} — reflected in invoice settlement where allocated`,
        debit: 0,
        credit: 0,
        reference: isRefund ? `REF-${p.id}` : `RCP-${p.id}`,
        rawItem: p,
      };
    }),
  ];

  // Sort chronological for running balance calculation
  ledgerEntries.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  let currentRunning = 0;
  const ledgerWithBalance = ledgerEntries.map((entry) => {
    currentRunning += entry.debit - entry.credit;
    return { ...entry, runningBalance: currentRunning };
  });

  // Reverse so newest is at top for display
  const displayLedger = [...ledgerWithBalance].reverse();

  const statusBadge = () => {
    if (owesUs > 0) {
      return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-3 py-1 text-xs font-bold text-red-700">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500" /> Owes {fmtMoney(owesUs)}
        </span>
      );
    }
    if (weOwe > 0) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 border border-rose-200 px-3 py-1 text-xs font-bold text-rose-700">
          <TrendingDown className="h-3.5 w-3.5 text-rose-500" /> Advance {fmtMoney(weOwe)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> All Settled
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb / Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/customers')}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
            title="Back to Customers"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Link to="/customers" className="text-xs font-semibold text-gray-500 hover:text-emerald-600">
                Customers
              </Link>
              <span className="text-xs text-gray-400">/</span>
              <span className="text-xs font-semibold text-gray-900">{customer.name}</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Customer Profile & Payments</h1>
          </div>
        </div>

        {/* Top Actions */}
        <div className="flex flex-wrap items-center gap-2.5">
          {weOwe > 0 && (
            <button
              onClick={() => openRefundPaymentModal(weOwe)}
              className="flex items-center gap-2 rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-rose-600 active:scale-[0.98]"
            >
              <RotateCcw className="h-4 w-4" /> Refund Advance ({fmtMoney(weOwe)})
            </button>
          )}
          <button
            onClick={() => openReceivePaymentModal(owesUs > 0 ? owesUs : null)}
            className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.98]"
          >
            <Banknote className="h-4 w-4" /> Receive Payment
          </button>
          <button
            onClick={openEditModal}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <Edit3 className="h-4 w-4 text-gray-500" /> Edit
          </button>
          {owesUs > 0 && (
            <button
              onClick={() => setShowClearBalance(true)}
              className="flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm font-semibold text-amber-800 shadow-sm transition-colors hover:bg-amber-100"
            >
              <RotateCcw className="h-4 w-4" /> Clear Dues
            </button>
          )}
          <button
            onClick={() => setShowDeleteCustomer(true)}
            className="flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-2.5 text-sm font-semibold text-red-600 shadow-sm transition-colors hover:bg-red-50"
            title="Delete Customer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Customer Header Card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            <div className={clsx('flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl text-2xl font-bold', avatarColors[customer.id % avatarColors.length])}>
              {customer.name.charAt(0).toUpperCase()}
            </div>
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">{customer.name}</h2>
                {statusBadge()}
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-800">{customer.phone || 'No phone number'}</span>
                </div>

                {customer.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{customer.email}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{customer.address}</span>
                  </div>
                )}

              </div>

              {customer.notes && (
                <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600 max-w-2xl">
                  <span className="font-semibold text-gray-700">Note: </span>
                  {customer.notes}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-start md:items-end gap-2 border-t border-gray-100 pt-4 md:border-0 md:pt-0">
            <div className="text-left md:text-right">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Customer Since</p>
              <p className="text-sm font-semibold text-gray-700">{fmtDate(customer.created_at || new Date())}</p>
            </div>
            {parseFloat(customer.credit_limit || 0) > 0 && (
              <div className="text-left md:text-right">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Credit Limit</p>
                <p className="text-sm font-semibold text-gray-700">{fmtMoney(customer.credit_limit)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Financial Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Outstanding Balance */}
        <div className={clsx(
          'rounded-2xl border p-5 shadow-sm transition-all',
          owesUs > 0 ? 'border-red-200 bg-red-50/50' : weOwe > 0 ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-white'
        )}>
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
              {owesUs > 0 ? 'Pending' : weOwe > 0 ? 'Advance' : 'Net Balance'}
            </p>
            <div className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-xl',
              owesUs > 0 ? 'bg-red-100 text-red-600' : weOwe > 0 ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
            )}>
              <Banknote className="h-4 w-4" />
            </div>
          </div>
          <p className={clsx(
            'mt-2 text-2xl font-extrabold',
            owesUs > 0 ? 'text-red-600' : weOwe > 0 ? 'text-blue-600' : 'text-emerald-600'
          )}>
            {fmtMoney(Math.abs(balance))}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-gray-500">
              {owesUs > 0 ? 'Pending dues' : weOwe > 0 ? 'Advance credit balance' : 'Zero balance'}
            </span>
            {owesUs > 0 && (
              <button
                onClick={() => openReceivePaymentModal(owesUs)}
                className="font-bold text-red-600 hover:underline"
              >
                Pay Now &rarr;
              </button>
            )}
            {weOwe > 0 && (
              <button
                onClick={() => openRefundPaymentModal(weOwe)}
                className="font-bold text-rose-600 hover:underline"
              >
                Refund Advance &rarr;
              </button>
            )}
          </div>
        </div>

        {/* Total Invoiced */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Invoiced</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
              <Receipt className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-gray-900">{fmtMoney(totalInvoiced)}</p>
          <p className="mt-2 text-xs text-gray-500">{sales.length} total invoice{sales.length === 1 ? '' : 's'}</p>
        </div>

        {/* Total Payments Received */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Applied to Invoices</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
              <CreditCard className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-emerald-600">{fmtMoney(totalSalesPaid)}</p>
          <p className="mt-2 text-xs text-gray-500">
            {payments.filter((p) => p.payment_type !== 'refund').length} receipt{payments.filter((p) => p.payment_type !== 'refund').length === 1 ? '' : 's'} recorded: {fmtMoney(totalDirectPayments)}
            {totalRefunds > 0 ? ` • ${fmtMoney(totalRefunds)} refunded` : ''}
          </p>
        </div>

        {/* Pending Invoices */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Gross Unpaid Invoices</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-amber-600">{unpaidSalesCount}</p>
          <p className="mt-2 text-xs text-gray-500">{fmtMoney(unpaidSalesTotal)} due on open invoices</p>
          {invoiceRefunds > 0 && <p className="mt-1 text-[11px] text-gray-500">{fmtMoney(invoiceRefunds)} paid out as customer refunds</p>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
        <button
          onClick={() => setActiveTab('invoices')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'invoices' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <Receipt className="h-4 w-4" /> Sales & Invoices ({sales.length})
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'payments' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <CreditCard className="h-4 w-4" /> Payments & Refunds ({payments.length})
        </button>
        <button
          onClick={() => setActiveTab('ledger')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'ledger' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <FileSpreadsheet className="h-4 w-4" /> Customer Ledger Statement
        </button>
      </div>

      {/* TAB 1: INVOICES & SALES */}
      {activeTab === 'invoices' && (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoice number..."
                value={salesSearch}
                onChange={(e) => setSalesSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:bg-white focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <SearchableSelect
                value={salesStatusFilter}
                onChange={(val) => setSalesStatusFilter(val)}
                options={[
                  { value: 'all', label: 'All Statuses' },
                  { value: 'paid', label: 'Paid' },
                  { value: 'partial', label: 'Partial' },
                  { value: 'unpaid', label: 'Unpaid' },
                ]}
                placeholder="Filter by Status"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Invoice #</th>
                  <th className="px-6 py-3 whitespace-nowrap">Date</th>
                  <th className="px-6 py-3 whitespace-nowrap">Status</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right">Total Amount</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right">Paid</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right">Due Balance</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSales.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                      No invoices found matching the criteria.
                    </td>
                  </tr>
                ) : (
                  filteredSales.map((s) => {
                    const total = parseFloat(s.total) || 0;
                    const paid = parseFloat(s.paid_amount) || 0;
                    const due = Math.max(0, total - paid);
                    return (
                      <tr key={s.id} className="group transition-colors hover:bg-gray-50/50">
                        <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => openInvoiceDetails(s)}
                            className="font-mono text-xs font-bold text-emerald-600 hover:underline"
                          >
                            {s.invoice_number}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">{fmtDate(s.sale_date || s.created_at)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={clsx(
                              'inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                              s.payment_status === 'paid'
                                ? 'bg-emerald-100 text-emerald-700'
                                : s.payment_status === 'partial'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                            )}
                          >
                            {s.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">{fmtMoney(total)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-emerald-600">{fmtMoney(paid)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-red-600">{due > 0 ? fmtMoney(due) : '-'}</td>
                        <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {due > 0 && (
                              <button
                                onClick={() => openReceivePaymentModal(due)}
                                className="flex items-center gap-1 rounded-lg bg-emerald-50 border border-emerald-200 px-2 py-1 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                                title="Collect payment for this invoice"
                              >
                                <Banknote className="h-3.5 w-3.5" /> Pay
                              </button>
                            )}
                            <button
                              onClick={() => handlePrintInvoice(s)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                              title="Print Invoice / PDF"
                            >
                              <Printer className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openInvoiceDetails(s)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600"
                              title="View Invoice Breakdown"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: PAYMENTS & REFUNDS */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-bold text-gray-900">Payments & Refunds for {customer.name}</h3>
              <p className="text-xs text-gray-500">Record payments received or refund advance balances back to the customer.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => openReceivePaymentModal(owesUs > 0 ? owesUs : null)}
                className="flex items-center gap-1.5 rounded-xl bg-emerald-500 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-600"
              >
                <Banknote className="h-4 w-4" /> Receive Payment
              </button>
              <button
                onClick={() => openRefundPaymentModal(weOwe > 0 ? weOwe : null)}
                className="flex items-center gap-1.5 rounded-xl bg-rose-500 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rose-600"
              >
                <RotateCcw className="h-4 w-4" /> Refund Advance
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Receipt / Date</th>
                    <th className="px-6 py-3 whitespace-nowrap">Type</th>
                    <th className="px-6 py-3 whitespace-nowrap">Payment Method</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right">Amount</th>
                    <th className="px-6 py-3 whitespace-nowrap">Notes & Reference</th>
                    <th className="px-6 py-3 whitespace-nowrap">Recorded At</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                        No payments recorded yet for this customer.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p) => {
                      const isRefund = p.payment_type === 'refund';
                      return (
                        <tr key={p.id} className="group transition-colors hover:bg-gray-50/50">
                          <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                            <div className="font-semibold text-gray-900">{fmtDate(p.payment_date)}</div>
                            <div className="text-[11px] text-gray-400 font-mono">{isRefund ? `REF-${p.id}` : `RCP-${p.id}`}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isRefund ? (
                              <span className="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-xs font-bold text-rose-700">
                                Advance Refund
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
                                Payment Received
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold capitalize text-blue-700">
                              {p.payment_method || 'cash'}
                            </span>
                          </td>
                          <td className={clsx('px-6 py-4 whitespace-nowrap text-right font-extrabold text-base', isRefund ? 'text-rose-600' : 'text-emerald-600')}>
                            {isRefund ? '- ' : '+ '}{fmtMoney(p.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-xs max-w-xs truncate">
                            {p.notes || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-400 text-xs">
                            {fmtDateTime(p.created_at)}
                          </td>
                          <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => setDeletePaymentItem(p)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                              title="Delete Payment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: STATEMENT / LEDGER */}
      {activeTab === 'ledger' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-gray-900">Statement of Account & Ledger</h3>
              <p className="text-xs text-gray-500">Chronological transaction audit trail with debit, credit, and running balance.</p>
            </div>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <Printer className="h-3.5 w-3.5" /> Print Statement
            </button>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Date</th>
                    <th className="px-6 py-3 whitespace-nowrap">Transaction</th>
                    <th className="px-6 py-3 whitespace-nowrap">Reference / Notes</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right">Debit (+)</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right">Credit (-)</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Running Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayLedger.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                        No transactions recorded for this customer.
                      </td>
                    </tr>
                  ) : (
                    displayLedger.map((entry) => (
                      <tr key={entry.id} className="group transition-colors hover:bg-gray-50/50">
                        <td className="sticky-col-left px-6 py-4 whitespace-nowrap text-xs text-gray-600">{fmtDate(entry.date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span
                              className={clsx(
                                'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
                                entry.type === 'invoice' ? 'bg-purple-100 text-purple-700' : entry.type === 'refund' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                              )}
                            >
                              {entry.type === 'invoice' ? 'INV' : entry.type === 'refund' ? 'REF' : 'PAY'}
                            </span>
                            <span className="font-semibold text-gray-900 text-xs">{entry.title}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">{entry.details}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                          {entry.debit > 0 ? fmtMoney(entry.debit) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-emerald-600">
                          {entry.credit > 0 ? fmtMoney(entry.credit) : '-'}
                        </td>
                        <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right font-extrabold text-gray-900">
                          {fmtMoney(entry.runningBalance)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* RECEIVE / REFUND PAYMENT MODAL */}
      {showReceivePayment &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReceivePayment(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {paymentForm.payment_type === 'refund' ? 'Refund Advance to Customer' : 'Receive Customer Payment'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {paymentForm.payment_type === 'refund'
                      ? `Pay out advance money to ${customer.name} (deducted from account)`
                      : `Record payment received from ${customer.name}`}
                  </p>
                </div>
                <button
                  onClick={() => setShowReceivePayment(false)}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Type selector */}
              <div className="mb-4 flex rounded-xl bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setPaymentForm({
                    ...paymentForm,
                    payment_type: 'received',
                    amount: owesUs > 0 ? String(owesUs) : '',
                    notes: paymentForm.notes === 'Advance refund paid to customer' ? '' : paymentForm.notes,
                  })}
                  className={clsx(
                    'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                    paymentForm.payment_type === 'received'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Banknote className="h-3.5 w-3.5" /> Receive Payment
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentForm({
                    ...paymentForm,
                    payment_type: 'refund',
                    amount: weOwe > 0 ? String(weOwe) : '',
                    notes: !paymentForm.notes ? 'Advance refund paid to customer' : paymentForm.notes,
                  })}
                  className={clsx(
                    'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                    paymentForm.payment_type === 'refund'
                      ? 'bg-rose-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Refund Advance
                </button>
              </div>

              {/* Status banner */}
              {paymentForm.payment_type === 'refund' ? (
                <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Advance Owed to Customer:</span>
                    <span className="text-sm font-extrabold">{fmtMoney(weOwe)}</span>
                  </div>
                  <p className="text-[11px] text-rose-700">
                    This refund will be <strong>deducted from your Cash / Bank balance</strong> in accounting.
                  </p>
                </div>
              ) : (
                <div className={clsx(
                  'mb-4 rounded-xl border p-3 text-xs',
                  owesUs > 0 ? 'border-red-200 bg-red-50 text-red-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                )}>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Current Pending Balance:</span>
                    <span className="text-sm font-extrabold">{fmtMoney(owesUs)}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleReceivePayment} className="space-y-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    {paymentForm.payment_type === 'refund' ? 'Refund Amount (Rs.) *' : 'Payment Amount (Rs.) *'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    placeholder="Enter amount (Rs.)"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-base font-bold text-gray-900 focus:border-emerald-500 focus:bg-white focus:outline-none"
                    autoFocus
                  />
                  {/* Quick pre-set amount chips */}
                  {paymentForm.payment_type === 'refund' && weOwe > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, amount: String(weOwe) })}
                        className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100"
                      >
                        Full Advance ({fmtMoney(weOwe)})
                      </button>
                    </div>
                  )}
                  {paymentForm.payment_type === 'received' && owesUs > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, amount: String(owesUs) })}
                        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-100"
                      >
                        Full Dues ({fmtMoney(owesUs)})
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Payment Date
                    </label>
                    <input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Payment Method
                    </label>
                    <div className="flex gap-2">
                      {['cash', 'card'].map((m) => (
                        <button
                          type="button"
                          key={m}
                          onClick={() => setPaymentForm({ ...paymentForm, payment_method: m })}
                          className={clsx(
                            'flex-1 py-2 text-xs font-semibold rounded-lg border transition-colors capitalize',
                            paymentForm.payment_method === m
                              ? paymentForm.payment_type === 'refund'
                                ? 'bg-rose-500 text-white border-rose-500'
                                : 'bg-emerald-500 text-white border-emerald-500'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                          )}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {paymentForm.payment_type === 'received' && (
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Apply payment to</label>
                    <select value={paymentForm.sale_id} onChange={(e) => setPaymentForm({ ...paymentForm, sale_id: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none">
                      <option value="">Oldest unpaid invoice automatically</option>
                      {sales.filter((s) => Number(s.total) > Number(s.paid_amount || 0)).map((s) => (
                        <option key={s.id} value={s.id}>#{s.invoice_number} — due {fmtMoney(Math.max(0, Number(s.total) - Number(s.paid_amount || 0)))}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Notes / Reference
                  </label>
                  <textarea
                    rows={2}
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder={paymentForm.payment_type === 'refund' ? 'Reason for advance return / payout...' : 'e.g. Receipt #, Bank txn ref, Cheque #'}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowReceivePayment(false)}
                    className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={clsx(
                      'flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm active:scale-[0.98]',
                      paymentForm.payment_type === 'refund'
                        ? 'bg-rose-500 hover:bg-rose-600'
                        : 'bg-emerald-500 hover:bg-emerald-600'
                    )}
                  >
                    {paymentForm.payment_type === 'refund' ? (
                      <><RotateCcw className="h-4 w-4" /> Refund & Deduct from Account</>
                    ) : (
                      <><Banknote className="h-4 w-4" /> Save Payment</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT CUSTOMER MODAL */}
      {showEditCustomer &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEditCustomer(false)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Customer Profile</h3>
                <button onClick={() => setShowEditCustomer(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSaveCustomer} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Full Name *</label>
                    <input
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone</label>
                    <input
                      value={editForm.phone || ''}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance (Rs.)</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.we_owe || '0'}
                      onChange={(e) => setEditForm({ ...editForm, we_owe: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Pending (Rs.)</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.they_owe || '0'}
                      onChange={(e) => setEditForm({ ...editForm, they_owe: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
                    <input
                      value={editForm.address || ''}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label>
                    <textarea
                      value={editForm.notes || ''}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                    />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditCustomer(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                  >
                    <Save className="h-4 w-4" /> Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* CLEAR BALANCE MODAL */}
      {showClearBalance &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowClearBalance(false)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
                <RotateCcw className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Clear Customer Balance?</h3>
              <p className="mt-2 text-sm text-gray-500">
                This will reset <strong>{customer.name}</strong>'s outstanding balance of <strong>{fmtMoney(owesUs)}</strong> to <strong>Rs. 0.00</strong>.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setShowClearBalance(false)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearBalance}
                  className="rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700"
                >
                  Clear Balance
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE CUSTOMER CONFIRM MODAL */}
      {showDeleteCustomer &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteCustomer(false)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Customer?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete <strong>{customer.name}</strong>? This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setShowDeleteCustomer(false)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteCustomer}
                  className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE PAYMENT CONFIRM MODAL */}
      {deletePaymentItem &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeletePaymentItem(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Payment?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this payment of <strong>{fmtMoney(deletePaymentItem.amount)}</strong>? The customer's balance will be adjusted accordingly.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setDeletePaymentItem(null)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePayment}
                  className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Delete Payment
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* VIEW INVOICE DETAIL MODAL */}
      {viewInvoice &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewInvoice(null)} />
            <div className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Invoice {viewInvoice.invoice_number}</h3>
                  <p className="text-xs text-gray-500">{fmtDateTime(viewInvoice.created_at)}</p>
                </div>
                <button onClick={() => setViewInvoice(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Items Breakdown */}
              {viewInvoice.items && viewInvoice.items.length > 0 && (
                <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-600">Purchased Items</h4>
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="pb-1">Item</th>
                        <th className="pb-1 text-center">Qty</th>
                        <th className="pb-1 text-right">Price</th>
                        <th className="pb-1 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {viewInvoice.items.map((it, idx) => (
                        <tr key={idx} className="text-gray-700">
                          <td className="py-1.5 font-medium">{it.medicine_name || `Medicine #${it.medicine_id}`}</td>
                          <td className="py-1.5 text-center">{it.quantity}</td>
                          <td className="py-1.5 text-right">{fmtMoney(it.unit_price)}</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">{fmtMoney(it.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Customer</span>
                  <span className="font-semibold text-gray-900">{customer.name}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-900">{fmtMoney(viewInvoice.subtotal)}</span>
                </div>
                {Number(viewInvoice.discount) > 0 && (
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-500">Discount</span>
                    <span className="text-red-500">- {fmtMoney(viewInvoice.discount)}</span>
                  </div>
                )}
                {Number(viewInvoice.tax) > 0 && (
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-500">Tax</span>
                    <span className="text-gray-900">{fmtMoney(viewInvoice.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-bold text-gray-900">Grand Total</span>
                  <span className="text-lg font-extrabold text-emerald-600">{fmtMoney(viewInvoice.total)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Paid Amount</span>
                  <span className="font-semibold text-gray-900">{fmtMoney(viewInvoice.paid_amount)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Balance Due</span>
                  <span className={clsx('font-bold', (parseFloat(viewInvoice.total) - parseFloat(viewInvoice.paid_amount || 0)) > 0 ? 'text-red-600' : 'text-gray-700')}>
                    {fmtMoney(Math.max(0, parseFloat(viewInvoice.total) - parseFloat(viewInvoice.paid_amount || 0)))}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Payment Method</span>
                  <span className="capitalize text-gray-900">{viewInvoice.payment_method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <span
                    className={clsx(
                      'font-semibold capitalize',
                      viewInvoice.payment_status === 'paid'
                        ? 'text-emerald-600'
                        : viewInvoice.payment_status === 'partial'
                          ? 'text-amber-600'
                          : 'text-red-600'
                    )}
                  >
                    {viewInvoice.payment_status}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setViewInvoice(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => handlePrintInvoice(viewInvoice)}
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-95 transition-all"
                >
                  <Printer className="h-4 w-4" /> Print Invoice
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ══════ SALE INVOICE MODAL & PRINT PORTAL ══════ */}
      {printSale &&
        createPortal(
          <SaleInvoice
            sale={printSale}
            customer={customer}
            onClose={() => setPrintSale(null)}
          />,
          document.body
        )}

      {/* ══════ DEDICATED PRINT CONTAINER FOR CUSTOMER STATEMENT ══════ */}
      {activeTab === 'ledger' &&
        createPortal(
          <div id="printable-statement-export" className="hidden print:block" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 30, color: '#1a1a1a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #10b981', paddingBottom: 16 }}>
              <div>
                <PharmacyLogo logo={settings?.pharmacy_logo} alt="Shop logo" className="h-10 w-10 rounded-lg mb-2" imageClassName="h-10 w-10 rounded-lg object-contain mb-2" />
                <h1 style={{ fontSize: 22, color: '#10b981', margin: 0, fontWeight: 800 }}>{settings?.pharmacy_name || 'Gaza Pharmacy'}</h1>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>Customer Statement & Ledger</p>
                {settings?.pharmacy_address && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>{settings.pharmacy_address}</p>}
                {settings?.pharmacy_phone && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Tel: {settings.pharmacy_phone}</p>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{customer?.name}</p>
                {customer?.phone && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Phone: {customer.phone}</p>}
                <p style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 0' }}>Generated: {formatPakistanDateTime(new Date())}</p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 16 }}>
              <thead>
                <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '8px 10px' }}>Date</th>
                  <th style={{ padding: '8px 10px' }}>Transaction</th>
                  <th style={{ padding: '8px 10px' }}>Reference / Details</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Debit (+)</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Credit (-)</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {displayLedger.map((entry, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 10px', color: '#4b5563' }}>{fmtDate(entry.date)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{entry.title}</td>
                    <td style={{ padding: '8px 10px', color: '#6b7280' }}>{entry.details}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{entry.debit > 0 ? fmtMoney(entry.debit) : '-'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{entry.credit > 0 ? fmtMoney(entry.credit) : '-'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{fmtMoney(entry.runningBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 24, textAlign: 'center', fontSize: 10, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
              {settings?.pharmacy_name || 'Gaza Pharmacy'} Management System — Statement Generated on {formatPakistanDateTime(new Date())}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
